import React, { useState, useEffect, useRef } from "react";
import { get, post } from "../../../services/api";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

const MedicalForm = ({ selectedStudent }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const roleId = localStorage.getItem("roleId");
  const userIdFromUrl = searchParams.get("userId");

  const studentFirstRegistrationId = selectedStudent.studentFirstRegistrationId;
  const userId = String(selectedStudent.userId);

  useEffect(() => {
    if (!userId) {
      console.log("User ID is missing from selectedStudent prop.");
    }
    if (!studentFirstRegistrationId) {
      console.log(
        "Student ID (firstRegistrationId) is missing from selectedStudent prop."
      );
    }
  }, [userId, studentFirstRegistrationId, navigate]);

  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
    setValue,
    reset,
    getValues,
  } = useForm({
    defaultValues: {
      id: 0,
      firstRegistrationId: 0,
      createdBy: userId || "",
      updatedBy: userId || "",
      medicalConditions: {},
      medicalConditionsPart2: {},
      medicalConditionsPart3: {},
      medicalOtherInformations: "",
      medicalAuthorizations1: {},
      medicalAuthorizations2: {},
      studentMedicalDocuments: [],
    },
  });

  const [medicalConditions, setMedicalConditions] = useState([]);
  const [medicalConditionsPart2, setMedicalConditionsPart2] = useState([]);
  const [medicalConditionsPart3, setMedicalConditionsPart3] = useState([]);
  const [medicalAuthorizations1, setMedicalAuthorizations1] = useState([]);
  const [medicalAuthorizations2, setMedicalAuthorizations2] = useState([]);
  const [contactsData, setContactsData] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const getContactsList = async () => {
    if (!token || !userId) {
      console.error("Unauthorized or User ID missing.");
      return;
    }
    try {
      const response = await get(
        `Student/GetStudentFamilyInformationByUserId?userId=${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response && response.length > 0) {
        setContactsData(response);
      }
    } catch (error) {
      console.error("Error fetching contacts:", error);
    }
  };

  const fetchMedicalInfoCheckboxes = async () => {
    try {
      const response = await get(
        "StudentMedicalInformation/GetMedicalCondition",
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response) {
        const medicalPart1 = response.medicalConditions.filter(
          (condition) => condition.id <= 13
        );
        const medicalPart2 = response.medicalConditions.filter(
          (condition) => condition.id >= 14 && condition.id < 23
        );
        const medicalPart3 = response.medicalConditions.filter(
          (condition) => condition.id >= 23
        );
        const authPart1 = response.medicalAuthorizations.filter(
          (condition) => condition.id <= 5
        );
        const authPart2 = response.medicalAuthorizations.filter(
          (condition) => condition.id >= 6
        );

        setMedicalConditions(medicalPart1);
        setMedicalConditionsPart2(medicalPart2);
        setMedicalConditionsPart3(medicalPart3);
        setMedicalAuthorizations1(authPart1);
        setMedicalAuthorizations2(authPart2);

        // Initialize form state for all checkboxes
        const formData = {
          medicalConditions: Object.fromEntries(
            medicalPart1.map((c) => [c.id, false])
          ),
          medicalConditionsPart2: Object.fromEntries(
            medicalPart2.map((c) => [c.id, false])
          ),
          medicalConditionsPart3: Object.fromEntries(
            medicalPart3.map((c) => [c.id, false])
          ),
          medicalAuthorizations1: Object.fromEntries(
            authPart1.map((a) => [a.id, false])
          ),
          medicalAuthorizations2: Object.fromEntries(
            authPart2.map((a) => [a.id, false])
          ),
        };
        // Reset form with initial empty checkbox values
        reset(formData, { keepDefaultValues: true });
      }
    } catch (error) {
      console.error("Error fetching medical information:", error);
    }
  };

  const fetchStudentMedicalData = async () => {
    if (!studentFirstRegistrationId) {
      console.error("Student First Registration ID is missing.");
      return;
    }

    if (medicalConditions.length === 0 || medicalAuthorizations1.length === 0) {
      console.log("Checkbox definitions not loaded yet. Skipping fetch.");
      return;
    }

    try {
      const response = await get(
        `StudentMedicalInformation/GetStudentMedicalInformationsByFirstRegistrationId?studentFirstRegistrationId=${studentFirstRegistrationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const formData = {
        id: response.id || 0,
        firstRegistrationId:
          response.firstRegistrationId || parseInt(studentFirstRegistrationId),
        createdBy: response.createdBy || userId || "",
        updatedBy: userId || "",
        medicalConditions: Object.fromEntries(
          medicalConditions.map((c) => [c.id, false])
        ),
        medicalConditionsPart2: Object.fromEntries(
          medicalConditionsPart2.map((c) => [c.id, false])
        ),
        medicalConditionsPart3: Object.fromEntries(
          medicalConditionsPart3.map((c) => [c.id, false])
        ),
        medicalOtherInformations: response.medicalOtherInformations || "",
        medicalAuthorizations1: Object.fromEntries(
          medicalAuthorizations1.map((a) => [a.id, false])
        ),
        medicalAuthorizations2: Object.fromEntries(
          medicalAuthorizations2.map((a) => [a.id, false])
        ),
        studentMedicalDocuments: [],
      };

      (response.medicalConditions || []).forEach((condition) => {
        if (condition.id > 0 && !condition.isDeleted) {
          if (condition.id <= 13) {
            formData.medicalConditions[condition.id] = true;
          } else if (condition.id >= 14 && condition.id < 23) {
            formData.medicalConditionsPart2[condition.id] = true;
          } else if (condition.id >= 23) {
            formData.medicalConditionsPart3[condition.id] = true;
          }
        }
      });

      (response.medicalAuthorizations || []).forEach((auth) => {
        if (auth.id > 0 && !auth.isDeleted) {
          if (auth.id <= 5) {
            formData.medicalAuthorizations1[auth.id] = true;
          } else {
            formData.medicalAuthorizations2[auth.id] = true;
          }
        }
      });

      const documentsFromAPI = response.studentMedicalDocuments || [];
      const formattedDocuments = documentsFromAPI.map((doc) => ({
        fileName: doc.fileName,
        filePath: doc.filePath,
        documentType: doc.documentType,
        base64String: doc.base64String || "",
      }));
      setUploadedFiles(formattedDocuments);

      // Reset form with data from API
      reset(formData);
    } catch (error) {
      console.error("Error fetching student medical information:", error);
    }
  };

  useEffect(() => {
    getContactsList();
    fetchMedicalInfoCheckboxes();
  }, []);

  useEffect(() => {
    if (
      studentFirstRegistrationId &&
      medicalConditions.length > 0 &&
      medicalAuthorizations1.length > 0
    ) {
      fetchStudentMedicalData();
    }
  }, [
    studentFirstRegistrationId,
    medicalConditions,
    medicalConditionsPart2,
    medicalConditionsPart3,
    medicalAuthorizations1,
    medicalAuthorizations2,
  ]);

  const validateAtLeastOne = () => {
    const values = getValues("medicalAuthorizations2") || {};
    return (
      Object.values(values).some((checked) => checked) ||
      "This field is required."
    );
  };

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    if (!studentFirstRegistrationId) {
      toast.error("Student ID is missing. Cannot save data.");
      return;
    }

    const medicalConditionsPayload = [
      ...medicalConditions
        .filter((condition) => data.medicalConditions?.[condition.id])
        .map((condition) => ({
          id: condition.id,
          name: condition.name,
          description: condition.description,
        })),
      ...medicalConditionsPart2
        .filter((condition) => data.medicalConditionsPart2?.[condition.id])
        .map((condition) => ({
          id: condition.id,
          name: condition.name,
          description: condition.description,
        })),
      ...medicalConditionsPart3
        .filter((condition) => data.medicalConditionsPart3?.[condition.id])
        .map((condition) => ({
          id: condition.id,
          name: condition.name,
          description: condition.description,
        })),
    ];

    const medicalAuthorizationsPayload = [
      ...medicalAuthorizations1
        .filter((condition) => data.medicalAuthorizations1?.[condition.id])
        .map((condition) => ({
          id: condition.id,
          name: condition.name,
          description: condition.description,
        })),
      ...medicalAuthorizations2
        .filter((condition) => data.medicalAuthorizations2?.[condition.id])
        .map((condition) => ({
          id: condition.id,
          name: condition.name,
          description: condition.description,
        })),
    ];

    const formData = {
      ...data,
      id: data.id,

      firstRegistrationId: parseInt(studentFirstRegistrationId),
      createdBy: data.createdBy || userId,
      updatedBy: userId,
      medicalConditions: medicalConditionsPayload,
      medicalOtherInformations: data.medicalOtherInformations || "",
      medicalAuthorizations: medicalAuthorizationsPayload,
      studentMedicalDocuments: uploadedFiles,
    };

    console.log("Saving data:", formData);

    try {
      const response = await post(
        "StudentMedicalInformation/UpdateStudentMedicalInformation",
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.isSuccess) {
        toast.success("Success!");
      } else {
        toast.error(response.message || "An unexpected error occurred");
      }
    } catch (error) {
      console.error("Error saving medical information:", error);
      toast.error("Failed to save medical information.");
    }
  };

  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const filePromises = files.map((file, index) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve({
            fileName: `Medical Form-${
              uploadedFiles.length + index + 1
            }.${file.name.split(".").pop()}`,
            filePath: reader.result,
            documentType: file.type || "application/octet-stream",
            base64String: reader.result.split(",")[1],
          });
        };
        reader.readAsDataURL(file);
      });
    });

    const fileDataArray = await Promise.all(filePromises);
    setUploadedFiles((prev) => [...prev, ...fileDataArray]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handlePreviewFile = (file) => {
    const isPDF = file.fileName.toLowerCase().endsWith(".pdf");
    if (file.base64String) {
      const byteCharacters = atob(file.base64String);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: isPDF ? "application/pdf" : "image/jpeg",
      });
      const fileUrl = URL.createObjectURL(blob);
      window.open(fileUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(fileUrl), 5000);
    } else if (file.filePath) {
      const attachmentUrl =
        process.env.REACT_APP_LIVE_DOMAIN_URL +
        "/ExternalImages/" +
        file.filePath;
      window.open(attachmentUrl, "_blank");
    }
  };

  const handleRemoveFile = (index) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <form className="h-100" onSubmit={handleSubmit(saveData)}>
      <div className="profile-content-area">
        <div className="mb-5">
          <h5 className="mb-0 fw-bold">Medical Information</h5>
        </div>

        <div className="profile-scrollable-area p-1">
          {/* <h5 className="mb-3 p-2 px-3 text-secondary bg-brand-light rounded-pill">
            Added Contacts
          </h5> */}

          {/* <div className="row gx-3 gy-2">
            {contactsData.map((contact, index) => (
              <div className="col-md-4" key={index}>
                <div className="card h-100">
                  <div className="card-header fs-9 px-2 py-2 bg-light fw-bold">
                    <strong>{contact.relationShip}</strong>
                    {contact.isPrimary && (
                      <span className="badge bg-primary fs-9 ms-2">
                        Primary Contact
                      </span>
                    )}
                  </div>
                  <ul className="list-group list-group-flush">
                    <li className="list-group-item px-2 fs-9">
                      <strong>Name:</strong> {contact.firstName}{" "}
                      {contact.lastName}
                    </li>
                    <li className="list-group-item px-2 fs-9">
                      <strong>Email:</strong> {contact.email}
                    </li>
                    <li className="list-group-item px-2 fs-9">
                      <strong>Phone:</strong> {contact.phone}
                    </li>
                  </ul>
                </div>
              </div>
            ))}
          </div> */}

          <h5 className="mb-3 p-2 px-3 mt-4 text-secondary bg-brand-light rounded-pill">
            Medical Conditions
          </h5>

          <div className="row gx-3 gy-2">
            <div className="col-md-4">
              <h5 className="fs-9 mb-2">Does your child suffer from</h5>
              {medicalConditions.map((condition) => (
                <div key={condition.id}>
                  <label className="form-check fs-8">
                    <input
                      {...register(`medicalConditions.${condition.id}`)}
                      type="checkbox"
                      id={`condition-${condition.id}`}
                      className="form-check-input"
                    />
                    <span className="d-inline-block fs-9">
                      {condition.name}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            <div className="col-md-4">
              <h5 className="fs-9 mb-2">
                Has your child ever had any of the following diseases
              </h5>
              {medicalConditionsPart2.map((condition) => (
                <div key={condition.id}>
                  <label className="form-check fs-8">
                    <input
                      {...register(`medicalConditionsPart2.${condition.id}`)}
                      type="checkbox"
                      id={`condition-${condition.id}`}
                      className="form-check-input"
                    />
                    <span className="d-inline-block fs-9">
                      {condition.name}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            <div className="col-md-4">
              <h5 className="fs-9 mb-2">
                Has your child been inoculated against
              </h5>
              {medicalConditionsPart3.map((condition) => (
                <div key={condition.id}>
                  <label className="form-check fs-8">
                    <input
                      {...register(`medicalConditionsPart3.${condition.id}`)}
                      type="checkbox"
                      id={`condition-${condition.id}`}
                      className="form-check-input"
                    />
                    <span className="d-inline-block fs-9">
                      {condition.name}
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="col-md-12">
            <h5 className="fs-9 mb-2">
              If you answered "Yes" to any of the above medical conditions,
              please provide further details.
            </h5>
            <textarea
              {...register("medicalOtherInformations")}
              className="form-control"
              placeholder=""
            ></textarea>
            {errors.medicalOtherInformations && (
              <small className="text-danger">
                {errors.medicalOtherInformations.message}
              </small>
            )}
          </div>

          <h5 className="mb-3 p-2 px-3 mt-4 text-secondary bg-brand-light rounded-pill">
            Authorisation
          </h5>

          <div className="row gy-2">
            <div className="col-md-6">
              <h5 className="fs-9 mb-2">
                I hereby authorise the school to administer the below chosen
                listed medications at the discretion of the Medical staff.
              </h5>
              {medicalAuthorizations1.map((condition) => (
                <div key={condition.id}>
                  <label className="form-check fs-8">
                    <input
                      {...register(`medicalAuthorizations1.${condition.id}`)}
                      type="checkbox"
                      id={`auth-${condition.id}`}
                      className="form-check-input"
                    />
                    <span className="d-inline-block fs-9">
                      {condition.name}
                    </span>
                  </label>
                </div>
              ))}
            </div>
            <div className="col-md-6">
              <h5 className="fs-9 mb-2">
                In case of an emergency, we kindly ask your approval on any of
                the following: <span className="text-danger fs-8">*</span>
              </h5>
              {medicalAuthorizations2.map((condition) => (
                <div key={condition.id}>
                  <label className="form-check fs-8">
                    <input
                      {...register(`medicalAuthorizations2.${condition.id}`, {
                        validate: validateAtLeastOne,
                      })}
                      type="checkbox"
                      id={`auth-${condition.id}`}
                      className="form-check-input"
                    />
                    <span className="d-inline-block fs-9">
                      {condition.name}
                    </span>
                  </label>
                </div>
              ))}
              {errors.medicalAuthorizations2 && (
                <small className="text-danger">
                  {Object.values(errors.medicalAuthorizations2)[0]?.message}
                </small>
              )}
            </div>
          </div>

          <h5 className="mb-3 p-2 px-3 mt-4 text-secondary bg-brand-light rounded-pill">
            Medical Documents
          </h5>

          <div className="row gy-2">
            <div className="col-md-4">
              <label className="form-label">Upload Documents</label>
              <div className="attachment-wrapper">
                <div className="attachment-placeholder">
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    multiple
                    className="form-control mt-2"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    style={{ width: "100px" }}
                  />
                </div>
                {uploadedFiles.length > 0 && (
                  <div className="attachment-preview mt-2">
                    {uploadedFiles.map((file, index) => (
                      <div
                        key={index}
                        className="file-item mb-1 d-flex justify-content-between align-items-center"
                      >
                        <div className="att-file-name">{file.fileName}</div>
                        <div className="d-flex gap-1">
                          <span
                            className="attachment-action-btn preview-btn"
                            onClick={() => handlePreviewFile(file)}
                            data-tooltip-id="global-tooltip"
                            data-tooltip-content="Preview"
                          >
                            <i className="fa fa-eye fs-10"></i>
                          </span>
                          <span
                            className="attachment-action-btn remove-btn"
                            onClick={() => handleRemoveFile(index)}
                            data-tooltip-id="global-tooltip"
                            data-tooltip-content="Remove"
                          >
                            <i className="fa fa-remove fs-10"></i>
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="mt-auto">
          <div className="d-flex justify-content-end gap-3">
            <button
              className="btn btn-primary btn-sm"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default MedicalForm;
